import { SearchBarState } from '@v2/shared/lib/types';
import useResizeObserver from 'use-resize-observer';
import React, { createContext, Ref, FC, useCallback, useReducer } from 'react';

type Props = {
  children?: React.ReactNode;
};

export interface State {
  searchBar: SearchBarState;
  headerRef: Ref<HTMLElement> | undefined;
  headerHeight: number;
  viewportHeight: string;
}

const initialState = {
  searchBar: {
    isOpen: false,
    toggle: () => {},
    open: () => {},
    close: () => {},
    isFocused: false,
    focus: () => {},
    unfocus: () => {},
  },
  headerRef: undefined,
  headerHeight: 0,
  viewportHeight: 'calc(100vh - 0px)',
};

type Action = {
  type: 'OPEN_SEARCHBAR' | 'CLOSE_SEARCHBAR' | 'TOGGLE_SEARCHBAR' | 'FOCUS_SEARCHBAR' | 'UNFOCUS_SEARCHBAR';
};

function uiReducer(state: State, action: Action) {
  switch (action.type) {
    case 'OPEN_SEARCHBAR': {
      return {
        ...state,
        searchBar: {
          ...state.searchBar,
          isOpen: true,
        },
      };
    }
    case 'CLOSE_SEARCHBAR': {
      return {
        ...state,
        searchBar: {
          ...state.searchBar,
          isOpen: false,
        },
      };
    }
    case 'TOGGLE_SEARCHBAR': {
      return {
        ...state,
        searchBar: {
          ...state.searchBar,
          isOpen: !state.searchBar.isOpen,
        },
      };
    }
    case 'FOCUS_SEARCHBAR': {
      return {
        ...state,
        searchBar: {
          ...state.searchBar,
          isFocused: true,
        },
      };
    }
    case 'UNFOCUS_SEARCHBAR': {
      return {
        ...state,
        searchBar: {
          ...state.searchBar,
          isFocused: false,
        },
      };
    }
  }
}

export const UIContext = createContext<State>(initialState);
UIContext.displayName = 'UIContext';

export const UIProvider: FC<Props> = (props) => {
  const [state, dispatch] = useReducer(uiReducer, initialState);

  const { ref: headerRef, height: headerHeight = 0 } = useResizeObserver<HTMLElement>();
  const viewportHeight = `calc(100vh - ${headerHeight}px)`;

  const searchBar = {
    isOpen: state.searchBar.isOpen,
    isFocused: state.searchBar.isFocused,
    toggle: useCallback(() => dispatch({ type: 'TOGGLE_SEARCHBAR' }), [dispatch]),
    open: useCallback(() => dispatch({ type: 'OPEN_SEARCHBAR' }), [dispatch]),
    close: useCallback(() => dispatch({ type: 'CLOSE_SEARCHBAR' }), [dispatch]),
    focus: useCallback(() => dispatch({ type: 'FOCUS_SEARCHBAR' }), [dispatch]),
    unfocus: useCallback(() => dispatch({ type: 'UNFOCUS_SEARCHBAR' }), [dispatch]),
  };

  const value = {
    ...state,
    searchBar,
    headerRef,
    headerHeight,
    viewportHeight,
  };

  return <UIContext.Provider value={value} {...props} />;
};

export const useUI = () => {
  const context = React.useContext(UIContext);

  if (context === undefined) {
    throw new Error(`useUI must be used within a UIProvider`);
  }
  return context;
};

export const ManagedUIContext: FC<Props> = ({ children }) => <UIProvider>{children}</UIProvider>;
